import { useEffect, useState } from 'react';

import { Modal, type TypeModalProps, toastService } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { useLoadingStore } from '~/store';

import { TOAST_MESSAGE } from '~/constants';

import styles from './ImageViewModal.module.scss';

/**
 * 이미지 정보 객체에서 필요한 속성을 추출하는 함수 타입
 */
export type TypeImageItemExtractor<T> = {
  /** 고유 ID를 추출하는 함수 */
  getId: (item: T) => string | number;
  /** URL을 추출하는 함수 */
  getUrl: (item: T) => string;
};

type TypeImageViewModalProps<T> = {
  /** 모달 제목 */
  title: string;
  /**
   * 모달 크기
   * @default "medium"
   */
  size?: TypeModalProps['size'];
  /** 모달 열림/닫힘 상태 */
  isOpen: boolean;
  /** 이미지 정보 객체 목록 */
  imageList: T[];
  /** 이미지 정보 객체에서 필요한 속성을 추출하는 함수들 */
  extractor: TypeImageItemExtractor<T>;
  /** alt 텍스트 생성 함수
   * @default "이미지 {index}"
   */
  getAltText?: (item: T, index: number) => string;
  /** 모달 닫기 콜백 */
  onClose: () => void;
};

const ImageViewModal = <T,>({
  title,
  size = 'medium',
  isOpen,
  imageList,
  extractor,
  getAltText = (_, index) => `이미지 ${index + 1}`,
  onClose,
}: TypeImageViewModalProps<T>) => {
  const { setIsLoading } = useLoadingStore();

  const [selectedImage, setSelectedImage] = useState<T>(imageList[0]);
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    setIsLoading({ dimmed: false });
  }, [setIsLoading]);

  const handleChangeSelectedImage = (image: T) => {
    const currentId = extractor.getId(selectedImage);
    const newId = extractor.getId(image);

    if (currentId === newId) return;

    setIsLoading(false);
    setIsLoading({ dimmed: false });
    setIsImageLoading(true);
    setSelectedImage(image);

    const imageElement = document.getElementById(`image-${newId}`);
    if (imageElement) {
      imageElement.scrollIntoView({ behavior: 'smooth', inline: 'center' });
    }
  };

  const handleSelectedImageLoad = () => {
    setIsLoading(false);
    setIsImageLoading(false);
  };

  const handleSelectedImageError = () => {
    setIsLoading(false);
    setIsImageLoading(false);
    toastService.addToast({
      type: 'error',
      message: TOAST_MESSAGE.DEFAULT_ERROR,
    });
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const canScrollHorizontally = container.scrollWidth > container.clientWidth;

    if (canScrollHorizontally) {
      container.scrollLeft += e.deltaY;
    }
  };

  return (
    <Modal title={title} size={size} open={isOpen} onConfirm={onClose}>
      <div className={styles['modal-container']}>
        {imageList.length > 1 && (
          <div className={styles['image-list-wrapper']} onWheel={handleWheel}>
            {imageList?.map((image, index) => (
              <ThumbnailButton
                key={extractor.getId(image)}
                alt={getAltText(image, index)}
                image={image}
                selectedImage={selectedImage}
                extractor={extractor}
                onChangeImage={handleChangeSelectedImage}
              />
            ))}
          </div>
        )}
        <div className={styles['selected-image-wrapper']}>
          {isImageLoading && <div className={styles['selected-image-skeleton']} />}
          <img
            className={clsx(styles['selected-image'], isImageLoading && styles['loading'])}
            src={extractor.getUrl(selectedImage)}
            alt={title}
            onLoad={handleSelectedImageLoad}
            onError={handleSelectedImageError}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ImageViewModal;

type ThumbnailButtonProps<T> = {
  alt: string;
  image: T;
  selectedImage: T;
  extractor: TypeImageItemExtractor<T>;
  onChangeImage: (image: T) => void;
};

const ThumbnailButton = <T,>({ alt, image, selectedImage, extractor, onChangeImage }: ThumbnailButtonProps<T>) => {
  const [isLoading, setIsLoading] = useState(true);

  const imageId = extractor.getId(image);
  const selectedId = extractor.getId(selectedImage);
  const imageUrl = extractor.getUrl(image);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
  };

  return (
    <button
      className={clsx(styles['thumbnail-button'], selectedId === imageId && styles['active'])}
      type="button"
      aria-label={alt}
      onClick={() => onChangeImage(image)}
    >
      {isLoading && <div className={styles['thumbnail-skeleton']} />}
      <img
        id={`image-${imageId}`}
        className={clsx(styles['thumbnail-image'], isLoading && styles['loading'])}
        src={imageUrl}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </button>
  );
};
